// Pure cadence helper shared by the runtime upgrade hover repeater and the
// OHov1 player-like simulation. It expresses the time-domain progression a
// single repeating upgrade target follows while a continuous input (real
// hover, or a simulated MP assignment) is held:
//
//   - the first fire is the warm-up fire and consumes no interval
//   - subsequent fires consume the current base interval from an accumulator
//   - the base interval decays multiplicatively after each consumed fire,
//     clamped at a minimum
//
// The runtime repeater wraps this with a DOM timer and burst notifications;
// the simulation drives it with game-time deltas. Keep both consumers on the
// same algorithm so cadence semantics do not drift.

import {
  DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  UPGRADE_HOVER_REPEATER_MIN_INTERVAL_MS,
} from "./upgradeRepeatTiming";

export const UPGRADE_HOVER_REPEATER_DEFAULT_DECAY = 0.85;

export interface UpgradeRepeatCadenceConfig {
  readonly initialIntervalMs: number;
  readonly minIntervalMs: number;
  readonly decay: number;
}

export const DEFAULT_UPGRADE_REPEAT_CADENCE_CONFIG: UpgradeRepeatCadenceConfig =
  {
    initialIntervalMs: DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
    minIntervalMs: UPGRADE_HOVER_REPEATER_MIN_INTERVAL_MS,
    decay: UPGRADE_HOVER_REPEATER_DEFAULT_DECAY,
  };

export interface UpgradeRepeatCadenceState {
  currentBaseIntervalMs: number;
  accumulatedRepeatMs: number;
  hasFiredInitial: boolean;
}

export function createUpgradeRepeatCadenceState(
  config: UpgradeRepeatCadenceConfig,
): UpgradeRepeatCadenceState {
  return {
    currentBaseIntervalMs: config.initialIntervalMs,
    accumulatedRepeatMs: 0,
    hasFiredInitial: false,
  };
}

export function resetUpgradeRepeatCadenceState(
  state: UpgradeRepeatCadenceState,
  config: UpgradeRepeatCadenceConfig,
): void {
  state.currentBaseIntervalMs = config.initialIntervalMs;
  state.accumulatedRepeatMs = 0;
  state.hasFiredInitial = false;
}

export interface AdvanceUpgradeRepeatCadenceOptions {
  readonly maxFires?: number;
}

// Advances the cadence by `elapsedMs` of game/simulation time and returns the
// number of fires due. Mutates `state`. The first call after creation/reset
// emits the warm-up fire (no interval consumed); subsequent fires consume
// the current base interval from the accumulator and decay the base interval
// toward `config.minIntervalMs`.
export function advanceUpgradeRepeatCadence(
  state: UpgradeRepeatCadenceState,
  config: UpgradeRepeatCadenceConfig,
  elapsedMs: number,
  options?: AdvanceUpgradeRepeatCadenceOptions,
): number {
  const maxFires = options?.maxFires ?? Number.POSITIVE_INFINITY;
  if (maxFires <= 0) return 0;

  let fires = 0;
  if (!state.hasFiredInitial) {
    state.hasFiredInitial = true;
    fires += 1;
    if (fires >= maxFires) return fires;
  }

  if (Number.isFinite(elapsedMs) && elapsedMs > 0) {
    state.accumulatedRepeatMs += elapsedMs;
  }

  while (
    fires < maxFires &&
    state.accumulatedRepeatMs >= state.currentBaseIntervalMs
  ) {
    const consumedIntervalMs = state.currentBaseIntervalMs;
    state.accumulatedRepeatMs -= consumedIntervalMs;
    state.currentBaseIntervalMs = Math.max(
      config.minIntervalMs,
      consumedIntervalMs * config.decay,
    );
    fires += 1;
  }
  return fires;
}

export function advanceUpgradeRepeatCadenceAggregated(
  state: UpgradeRepeatCadenceState,
  config: UpgradeRepeatCadenceConfig,
  elapsedMs: number,
  options?: AdvanceUpgradeRepeatCadenceOptions,
): number {
  const maxFires = options?.maxFires ?? Number.POSITIVE_INFINITY;
  if (maxFires <= 0) return 0;

  let fires = 0;
  if (!state.hasFiredInitial) {
    state.hasFiredInitial = true;
    fires += 1;
    if (fires >= maxFires) return fires;
  }

  if (Number.isFinite(elapsedMs) && elapsedMs > 0) {
    state.accumulatedRepeatMs += elapsedMs;
  }

  while (
    fires < maxFires &&
    state.accumulatedRepeatMs >= state.currentBaseIntervalMs &&
    state.currentBaseIntervalMs > config.minIntervalMs
  ) {
    const consumedIntervalMs = state.currentBaseIntervalMs;
    state.accumulatedRepeatMs -= consumedIntervalMs;
    state.currentBaseIntervalMs = Math.max(
      config.minIntervalMs,
      consumedIntervalMs * config.decay,
    );
    fires += 1;
  }

  if (
    fires >= maxFires ||
    state.accumulatedRepeatMs < state.currentBaseIntervalMs
  ) {
    return fires;
  }

  const remainingFireBudget = maxFires - fires;
  const bulkFires = Math.min(
    remainingFireBudget,
    Math.floor(state.accumulatedRepeatMs / state.currentBaseIntervalMs),
  );
  state.accumulatedRepeatMs -= bulkFires * state.currentBaseIntervalMs;
  fires += bulkFires;
  return fires;
}

/**
 * Advances a cadence through `elapsedMs` under the assumption that every
 * fire completing strictly before `elapsedMs` would be a zero-purchase
 * (starved) fire — i.e. the caller has already proven affordability cannot
 * be reached before that point, so each of those fires would take the
 * runtime's reset-to-`initialIntervalMs` branch (see
 * `executeHovGainUpgradeMpAutomationSegment`'s zero-attempts-applied reset
 * in `hoverIdleModel.ts`). Produces the exact state a per-fire loop would
 * leave behind without stepping through each fire.
 *
 * A fire landing exactly at `elapsedMs` is deliberately left un-consumed
 * (the resulting state reads as ready, not reset): the caller folds up to a
 * boundary it does not itself know is starved beyond that instant, so the
 * fire at that instant must still go through the real per-fire purchase
 * check. Callers must not invoke this for a span that could contain an
 * affordable fire — that determination is the caller's responsibility, not
 * this function's.
 *
 * No-ops when the cadence has not fired its warm-up fire yet, matching the
 * raw-accumulation callers this is used alongside.
 */
export function advanceUpgradeRepeatCadenceStarved(
  state: UpgradeRepeatCadenceState,
  config: UpgradeRepeatCadenceConfig,
  elapsedMs: number,
): void {
  if (!state.hasFiredInitial || !Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    return;
  }

  const firstFireAtMs = Math.max(
    0,
    state.currentBaseIntervalMs - state.accumulatedRepeatMs,
  );
  if (elapsedMs <= firstFireAtMs) {
    state.accumulatedRepeatMs += elapsedMs;
    return;
  }

  const sinceFirstFireMs = elapsedMs - firstFireAtMs;
  const foldedFireCount = Math.ceil(
    sinceFirstFireMs / config.initialIntervalMs,
  );
  const lastFoldedFireAtMs =
    firstFireAtMs + (foldedFireCount - 1) * config.initialIntervalMs;
  state.currentBaseIntervalMs = config.initialIntervalMs;
  state.accumulatedRepeatMs = elapsedMs - lastFoldedFireAtMs;
}

export function calculateUpgradeRepeatCadenceFireLimit(input: {
  readonly elapsedMs: number;
  readonly config: UpgradeRepeatCadenceConfig;
  readonly maxFires: number;
}): number {
  if (
    !Number.isFinite(input.elapsedMs) ||
    input.elapsedMs < 0 ||
    !Number.isInteger(input.maxFires) ||
    input.maxFires <= 0
  ) {
    return 0;
  }

  const { initialIntervalMs, minIntervalMs, decay } = input.config;
  if (
    !Number.isFinite(initialIntervalMs) ||
    initialIntervalMs <= 0 ||
    !Number.isFinite(minIntervalMs) ||
    minIntervalMs <= 0 ||
    !Number.isFinite(decay) ||
    decay <= 0
  ) {
    return 0;
  }

  let fires = 1;
  let remainingMs = input.elapsedMs;
  let intervalMs = Math.max(minIntervalMs, initialIntervalMs);
  if (fires >= input.maxFires) return input.maxFires;

  if (decay >= 1) {
    return Math.min(
      input.maxFires,
      fires + Math.floor(remainingMs / intervalMs),
    );
  }

  while (
    fires < input.maxFires &&
    intervalMs > minIntervalMs &&
    remainingMs >= intervalMs
  ) {
    remainingMs -= intervalMs;
    fires += 1;
    intervalMs = Math.max(minIntervalMs, intervalMs * decay);
  }
  if (fires >= input.maxFires) return input.maxFires;

  return Math.min(input.maxFires, fires + Math.floor(remainingMs / intervalMs));
}
